/**
 * @format
 */

import React, { useRef, useEffect } from 'react';
import { Spin } from 'antd';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import ProfileIcon from 'img/profileRound.svg';
import First from 'img/1st.svg';
import Second from 'img/2nd.svg';
import Third from 'img/3rd.svg';
import { AgentLeaderboardItem } from '../types/leaderboard.types';
import WeeklySalesChart from './WeeklySalesChart';

interface PodiumProps {
  data: AgentLeaderboardItem[];
  loading: boolean;
  error?: any;
}

const Podium: React.FC<PodiumProps> = ({ data, loading, error }) => {
  const containerRef = useRef<HTMLDivElement>(null);

  // Sort data by total premium (all types combined)
  const sortedData = [...data].sort((a, b) => {
    const totalPremiumA =
      a.totalMajorMedPremium + a.totalPrivateMedPremium + a.totalAncillaryPremium;
    const totalPremiumB =
      b.totalMajorMedPremium + b.totalPrivateMedPremium + b.totalAncillaryPremium;
    return totalPremiumB - totalPremiumA;
  });

  const topThreeAgents = sortedData.slice(0, 3);

  // Horizontal scroll functionality
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let isDown = false;
    let startX = 0;
    let scrollLeft = 0;

    const end = () => {
      isDown = false;
      container.style.cursor = 'default';
      container.style.removeProperty('user-select');
    };

    const start = (e: MouseEvent | TouchEvent) => {
      isDown = true;
      container.style.cursor = 'grabbing';
      container.style.userSelect = 'none';
      startX = (e instanceof TouchEvent ? e.touches[0].pageX : e.pageX) || 0;
      scrollLeft = container.scrollLeft;
    };

    const move = (e: MouseEvent | TouchEvent) => {
      if (!isDown) return;
      e.preventDefault();
      const x = (e instanceof TouchEvent ? e.touches[0].pageX : e.pageX) || 0;
      const walk = (x - startX) * 2;
      container.scrollLeft = scrollLeft - walk;
    };

    container.addEventListener('mousedown', start as EventListener);
    container.addEventListener('mouseleave', end);
    container.addEventListener('mouseup', end);
    container.addEventListener('mousemove', move as EventListener);
    container.addEventListener('touchstart', start as EventListener);
    container.addEventListener('touchend', end);
    container.addEventListener('touchmove', move as EventListener);

    return () => {
      container.removeEventListener('mousedown', start as EventListener);
      container.removeEventListener('mouseleave', end);
      container.removeEventListener('mouseup', end);
      container.removeEventListener('mousemove', move as EventListener);
      container.removeEventListener('touchstart', start as EventListener);
      container.removeEventListener('touchend', end);
      container.removeEventListener('touchmove', move as EventListener);
    };
  }, []);

  const getAgentName = (agent: AgentLeaderboardItem) => {
    const { firstName, lastName } = agent.user;
    return `${firstName} ${lastName}`;
  };

  const PodiumCard: React.FC<{
    agent: AgentLeaderboardItem | undefined;
    place: 'gold' | 'silver' | 'bronze';
    placeImage: string;
  }> = ({ agent, place, placeImage }) => {
    if (!agent) {
      return (
        <div className={`podium ${place}`}>
          <span style={{ fontSize: '12px', fontWeight: 600 }}>No Agent</span>
          <div className='pf-l-s-cr-wrapper'>
            <div
              style={{
                width: '73px',
                height: '55px',
                backgroundImage: `url(${ProfileIcon})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                borderRadius: '50%',
                position: 'relative',
              }}>
              <img className='place' alt='number placement' src={placeImage} />
            </div>
            <div className='profile-stats-table'>
              <table>
                <thead>
                  <tr>
                    <th>Premium</th>
                    <th>ACA</th>
                    <th style={{ borderRight: 'none' }}>U65</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>$0</td>
                    <td>0</td>
                    <td style={{ borderRight: 'none' }}>0</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      );
    }

    const totalPremium =
      agent.totalPrivateMedPremium + agent.totalAncillaryPremium;

    return (
      <div className={`podium ${place}`}>
        <span style={{ fontSize: '12px', fontWeight: 600 }}>
          {place.charAt(0).toUpperCase() + place.slice(1)} - {getAgentName(agent)}
        </span>
        <div className='pf-l-s-cr-wrapper'>
          <div
            style={{
              width: '73px',
              height: '55px',
              backgroundImage: `url(${ProfileIcon})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              borderRadius: '50%',
              position: 'relative',
            }}>
            <img className='place' alt='number placement' src={placeImage} />
          </div>
          <div className='profile-stats-table'>
            <table>
              <thead>
                <tr>
                  <th>Premium</th>
                  <th>ACA</th>
                  <th style={{ borderRight: 'none' }}>U65</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>${totalPremium.toLocaleString()}</td>
                  <td>{agent.totalMajorMed}</td>
                  <td style={{ borderRight: 'none' }}>{agent.totalPrivateMed}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <span style={{ fontSize: '12px', fontWeight: 600 }}>Weekly Sales Activity</span>
        <WeeklySalesChart userId={agent.userId} style={{ width: '100%', height: '200px' }} />
      </div>
    );
  };

  if (loading) {
    return (
      <div className='top-three-container'>
        {[1, 2, 3].map((i) => (
          <div key={i} className='podium'>
            <Skeleton height={30} width={200} />
            <Skeleton circle={true} height={73} width={73} />
            <Skeleton height={30} width={200} />
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return <div style={{ color: 'red' }}>Error loading podium data</div>;
  }

  return (
    <div ref={containerRef} className='top-three-container'>
      <PodiumCard agent={topThreeAgents[0]} place='gold' placeImage={First} />
      <PodiumCard agent={topThreeAgents[1]} place='silver' placeImage={Second} />
      <PodiumCard agent={topThreeAgents[2]} place='bronze' placeImage={Third} />
    </div>
  );
};

export default Podium;
